import { ChevronLeft, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import useSWR from "swr";
import {
  createInternalUser,
  deleteInternalUser,
  updateInternalUser,
} from "../../../api/adminAPI";
import CourtOfficerLanding, {
  type CourtOfficerSummary,
} from "./CourtOfficerLanding";
import CourtOfficerModal from "./CourtOfficerModal";
import AssignCourtroomModal from "./AssignCourtroomModal";
import CourtOfficerTable from "./CourtOfficerTable";
import OfficerCreatedSuccessModal from "./OfficerCreatedSuccessModal";
import {
  DC_ORANGE,
  HC_BLUE,
  contextTitle,
  courtRoomLabel,
  emptyOfficerForm,
  highCourtLabel,
  isAssignableRole,
  officerId,
  officerName,
} from "./helpers";
import type {
  BenchRoomRef,
  CourtOfficer,
  CourtOfficerCourtType,
  CourtOfficerFormState,
  CourtOfficerRole,
  CourtRoomRef,
  HighCourtRef,
  DistrictRef,
} from "./types";
import OfficerEmailFailedModal from "./OfficerEmailFailedModal";

interface OfficersResponse {
  users?: CourtOfficer[];
  page?: number;
  pages?: number;
  total?: number;
  summary?: CourtOfficerSummary;
}

interface BenchRoomsResponse {
  rooms?: BenchRoomRef[];
}

interface CourtRoomsResponse {
  rooms?: CourtRoomRef[];
}

interface HighCourtsResponse {
  data?: HighCourtRef[];
}

interface DistrictsResponse {
  data?: DistrictRef[];
}

const limit = 10;

function apiMessage(error: unknown) {
  const apiError = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return (
    apiError.response?.data?.message ||
    apiError.message ||
    "Something went wrong"
  );
}

function buildOfficerPayload(
  form: CourtOfficerFormState,
  courtRooms: CourtRoomRef[],
  highCourts: HighCourtRef[],
) {
  const payload: Record<string, any> = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim().toLowerCase(),
    phone: form.phone.trim(),
    username: form.username.trim().toLowerCase(),
    role: form.role,
    courtType: form.courtType,
    status: form.status,
    stateName: form.stateName,
  };
  if (form.courtType === "high_courts") {
    payload.highcourt = form.highCourtId || "";

    payload.bench = form.role === "Judge" ? form.benchId : "";

    // Previous functionality
    payload.benchRoom =
      form.role === "Judge"
        ? form.assignmentId || ""
        : form.role === "Reader"
          ? form.courtrooms?.[0] || ""
          : "";

    // High Court Reader + Controller
    payload.benchRooms =
      form.role === "Reader" || form.role === "Controller"
        ? form.courtrooms || []
        : [];

    payload.district = "";
    payload.complex = "";
    payload.courtRoom = "";
    payload.courtrooms = [];
    payload.courtRoomNo = "";
  } else {
    payload.courtrooms =
      form.role === "Reader" || form.role === "Controller"
        ? form.courtrooms
        : [];

    const selectedRoom = courtRooms.find(
      (room) => String(room._id || room.id || "") === form.assignmentId,
    );

    payload.courtRoom = isAssignableRole(form.role) ? form.assignmentId : "";
    payload.courtRoomNo = selectedRoom ? courtRoomLabel(selectedRoom) : "";
    payload.district = form.districtId;
    payload.stateName = form.stateName;
    payload.complex = form.complexId;
    payload.highcourt = "";
    payload.bench = "";
  }

  if (form.role === "Judge" && form.highCourtId) {
    const selectedCourt = highCourts.find(
      (court) => String(court._id || court.id || "") === form.highCourtId,
    );
    payload.courtRoomNo = selectedCourt ? highCourtLabel(selectedCourt) : "";
  }

  if (form.role === "Judge" && form.districtId) {
    payload.courtRoomNo = "";
  }

  if (form.password) {
    payload.password = form.password;
  }

  return payload;
}

export default function CourtOfficersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const courtTypeParam = searchParams.get("courtType");
  const courtType: CourtOfficerCourtType | null =
    courtTypeParam === "high_courts" || courtTypeParam === "district_courts"
      ? courtTypeParam
      : null;
  const [activeRole, setActiveRole] = useState<CourtOfficerRole | "all">("all");
  const [status, setStatus] = useState("");
  const [assignment, setAssignment] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOfficer, setEditingOfficer] = useState<CourtOfficer | null>(
    null,
  );
  const [assignmentOfficer, setAssignmentOfficer] =
    useState<CourtOfficer | null>(null);
  const [deleteOfficer, setDeleteOfficer] = useState<CourtOfficer | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successModal, setSuccessModal] = useState<any>(null);
  const { data: landingData, mutate: mutateLanding } = useSWR<OfficersResponse>(
    "/api/admin/internalusers?page=1&limit=10",
  );

  const tableKey = courtType
    ? `/api/admin/internalusers?page=${page}&limit=${limit}&search=${encodeURIComponent(
      search,
    )}&courtType=${courtType}&role=${activeRole === "all" ? "" : activeRole
    }&status=${status}&assignment=${assignment}`
    : null;

  const {
    data: tableData,
    mutate: mutateTable,
    isLoading,
  } = useSWR<OfficersResponse>(tableKey);

  const { data: benchData } = useSWR<BenchRoomsResponse>(
    "/api/admin/get-benches?page=1&limit=50",
  );
  const { data: roomData } = useSWR<CourtRoomsResponse>(
    "/api/admin/courtrooms?page=1&limit=50",
  );
  const { data: highCourtData } = useSWR<HighCourtsResponse>(
    "/api/court/get-highcourt",
  );
  const { data: districtData } = useSWR<DistrictsResponse>(
    "/api/court/get-districts?page=1&limit=200",
  );

  const benchRooms = useMemo(() => benchData?.rooms || [], [benchData?.rooms]);
  const courtRooms = useMemo(() => roomData?.rooms || [], [roomData?.rooms]);
  const highCourts = useMemo(
    () => highCourtData?.data || [],
    [highCourtData?.data],
  );
  const districts = useMemo(
    () => districtData?.data || [],
    [districtData?.data],
  );
  const officers = tableData?.users || [];
  const landingOfficers = landingData?.users || [];
  const summary = tableData?.summary || landingData?.summary;

  const roleCounts = useMemo<Record<CourtOfficerRole | "all", number>>(() => {
    const current =
      courtType === "district_courts"
        ? summary?.districtCourt
        : summary?.highCourt;
    return {
      all: current?.total || 0,
      Judge: current?.judges || 0,
      Reader: current?.readers || 0,
      Controller: current?.controllers || 0,
    };
  }, [courtType, summary]);

  const accent = courtType === "district_courts" ? DC_ORANGE : HC_BLUE;

  const openCreate = () => {
    setEditingOfficer(null);
    setModalOpen(true);
  };

  const openEdit = (officer: CourtOfficer) => {
    setEditingOfficer(officer);
    setModalOpen(true);
  };
  const openAssign = (officer: CourtOfficer) => {
    setAssignmentOfficer(officer);
  };
  const resetFiltersForContext = (nextCourtType: CourtOfficerCourtType) => {
    setSearchParams({ courtType: nextCourtType });
    setActiveRole("all");
    setStatus("");
    setAssignment("");
    setSearch("");
    setPage(1);
  };

  const refreshData = async () => {
    await Promise.all([mutateTable(), mutateLanding()]);
  };

  const saveOfficer = async (form: CourtOfficerFormState) => {
    setIsSubmitting(true);
    try {
      const payload = editingOfficer
        ? {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          username: form.username.trim().toLowerCase(),
          role: form.role,
          courtType: form.courtType,
          status: form.status,
          stateName: form.stateName,
        }
        : buildOfficerPayload(form, courtRooms, highCourts);

      const response = editingOfficer
        ? await updateInternalUser({
          ...payload,
          id: officerId(editingOfficer),
        })
        : await createInternalUser(payload);

      console.log("url....",import.meta.env.VITE_APP_FRONTEND_URL);

      if (response.data?.status) {
        if (!editingOfficer) {
          setSuccessModal({
            firstName: payload.firstName,
            lastName: payload.lastName,
            username: payload.username,
            password: payload.password,
            email: payload.email,
            role: payload.role,
            courtType: payload.courtType,
            status: payload.status,
            emailSent: response.data.emailSent,
            emailError: response.data.emailError,
            loginUrl: window.location.origin,
          });
        } else {
          toast.success(
            response.data.message || "Court officer updated successfully",
          );
        }

        setModalOpen(false);
        setEditingOfficer(null);

        await refreshData();
        return;
      }
      toast.error(response.data?.message || "Unable to save officer");
    } catch (error) {
      toast.error(apiMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteOfficer) return;
    setIsDeleting(true);
    try {
      const response = await deleteInternalUser(officerId(deleteOfficer));
      if (response.data?.status) {
        toast.success(response.data.message || "Officer deleted");
        setDeleteOfficer(null);
        await refreshData();
        return;
      }
      toast.error(response.data?.message || "Unable to delete officer");
    } catch (error) {
      toast.error(apiMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-100px)] overflow-y-auto p-4 bg-[#eef4ff] text-slate-950">
      {!courtType ? (
        <CourtOfficerLanding
          officers={landingOfficers}
          summary={landingData?.summary}
          onSelect={resetFiltersForContext}
        />
      ) : (
        <div className="space-y-5">
          <div
            className="flex items-center justify-between rounded-lg px-5 py-3"
            style={{
              border: "1px solid #374151",
              backgroundColor:
                courtType === "district_courts" ? "#FFF4EE" : "#EFF6FF",
            }}
          >
            <div className="flex items-center gap-3">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: accent }}
              />

              <div>
                <div className="text-sm font-bold" style={{ color: accent }}>
                  {courtType === "district_courts"
                    ? "District Courts"
                    : "High Courts"}
                </div>

                <div className="text-xs text-slate-500">
                  Showing Judges, Readers & Controllers for{" "}
                  {courtType === "district_courts"
                    ? "District Courts"
                    : "High Courts"}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-white px-4 text-sm font-semibold transition hover:shadow-sm"
              style={{
                border: `1px solid ${accent}`,
                color: accent,
              }}
            >
              <ChevronLeft size={16} strokeWidth={2} />
              Change Court Type
            </button>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-700">
                {contextTitle(courtType)}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Judges, Readers & Controllers
              </p>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-[8px] px-5 text-sm font-semibold text-white transition"
              style={{
                background:
                  courtType === "district_courts" ? "#f26422" : "#2563EB",
              }}
            >
              + Create Officer
            </button>
          </div>

          <CourtOfficerTable
            officers={officers}
            courtType={courtType}
            activeRole={activeRole}
            search={search}
            status={status}
            assignment={assignment}
            page={tableData?.page || page}
            totalPages={tableData?.pages || 1}
            isLoading={isLoading}
            benchRooms={benchRooms}
            courtRooms={courtRooms}
            highCourts={highCourts}
            roleCounts={roleCounts}
            onRoleChange={(role) => {
              setActiveRole(role);
              setPage(1);
            }}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            onStatusChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            onAssignmentChange={(value) => {
              setAssignment(value);
              setPage(1);
            }}
            onPageChange={setPage}
            onAdd={openCreate}
            onEdit={openEdit}
            onAssign={openAssign}
            onDelete={setDeleteOfficer}
          />
        </div>
      )}

      <CourtOfficerModal
        isOpen={modalOpen}
        officer={editingOfficer}
        highCourts={highCourts}
        districts={districts}
        isSubmitting={isSubmitting}
        initialCourtType={courtType || emptyOfficerForm.courtType}
        onClose={() => {
          setModalOpen(false);
          setEditingOfficer(null);
        }}
        onSubmit={saveOfficer}
      />
      <AssignCourtroomModal
        isOpen={Boolean(assignmentOfficer)}
        officer={assignmentOfficer}
        highCourts={highCourts}
        courtRooms={courtRooms}
        districts={districts}
        onClose={() => setAssignmentOfficer(null)}
        onSave={async ({
          officerId,
          assignmentId,
          stateName,
          districtId,
          highCourtId,

          courtrooms,
        }: {
          officerId: string;
          assignmentId: string;
          stateName?: string;
          districtId?: string;
          highCourtId?: string;
          courtrooms?: string[];
        }) => {
          if (!assignmentOfficer) return;

          let courtRoomNo = "";

          if (assignmentOfficer.courtType === "high_courts") {
            const selectedCourt = highCourts.find(
              (court) => court._id === assignmentId,
            );

            courtRoomNo = selectedCourt?.highCourtName || "";
          } else {
            const selectedDistrict = districts.find(
              (district) => district._id === assignmentId,
            );

            courtRoomNo = selectedDistrict?.districtName || "";
          }

          const response = await updateInternalUser({
            id: officerId,

            firstName: assignmentOfficer.firstName,
            lastName: assignmentOfficer.lastName,
            email: assignmentOfficer.email,
            phone: assignmentOfficer.phone,
            username: assignmentOfficer.username,
            role: assignmentOfficer.role,
            status: assignmentOfficer.status,
            courtType: assignmentOfficer.courtType,

            courtRoom: "",
            complex: "",

            ...(assignmentOfficer.courtType === "high_courts"
              ? {
                highcourt: highCourtId || "",

                benchRoom:
                  assignmentOfficer.role === "Reader"
                    ? courtrooms?.[0] || assignmentId
                    : assignmentId,

                benchRooms: courtrooms || [],

                district: "",
                courtrooms: [],
              }
              : {
                highcourt: "",
                district: districtId,
                complex: assignmentId,
                stateName,
                bench: "",
                courtrooms: courtrooms || [],
              }),

            courtRoomNo,
          });
          if (response.data?.status) {
            toast.success(response.data.message);

            setAssignmentOfficer(null);

            await refreshData();
          }
        }}
      />
      {successModal?.emailSent ? (
        <OfficerCreatedSuccessModal
          isOpen={Boolean(successModal)}
          officer={successModal}
          onClose={() => setSuccessModal(null)}
        />
      ) : (
        <OfficerEmailFailedModal
          isOpen={Boolean(successModal)}
          officer={successModal}
          onClose={() => setSuccessModal(null)}
        />
      )}
      {deleteOfficer && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/35 px-4">
          <div className="w-full max-w-md rounded-[16px] bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 p-5">
              <div>
                <h2 className="text-lg font-bold text-slate-950">
                  Delete Court Officer
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDeleteOfficer(null)}
                className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-slate-200 text-slate-500"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to delete{" "}
                <b>{officerName(deleteOfficer)}</b>?
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setDeleteOfficer(null)}
                  className="h-11 rounded-[8px] border border-slate-200 text-sm font-semibold text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={confirmDelete}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[8px] bg-red-600 text-sm font-bold text-white disabled:opacity-60"
                >
                  <Trash2 size={16} />
                  {isDeleting ? "Deleting..." : "Delete Officer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}